import { pageViewTypes } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { getShowStudyPackViews } from "@helpers/exams";
/* -----------------Features--------------- */
import { isWeb } from "@helpers/platform";
import { getProgressCardsData } from "@helpers/progress-data";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useMixedItemDist from "@hooks/useMixedItemDist";
import ProgressApplet from "@memorang/ui/components/ProgressApplet";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
import { NestedExamChipsContainer } from "./NestedExamChipsContainer";
import ProgressLoadingContainer from "./ProgressLoadingContainer";

const ProgressContainer = () => {
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);
	const { handleSessionConfigNavigation } = useCustomNavigation();
	const { t } = useTranslation();

	const appFont = useAppStore((store) => store.app.font) || "opensans";

	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const showPracticeTests = useAppStore((state) => state.app.showPracticeTests);
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const theme = useAppTheme();
	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.progress,
		};
	}, []);

	useAnalytics(memoizedAnalyticsParams);

	if (isLoading || !mixedItemDist) {
		return <ProgressLoadingContainer />;
	}

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const {
		masteryTagsProgressData,
		needsImprovementProgressData,
		progressPageSummaryCardData,
		practiceTestsCardsData,
		showEmptyState,
	} = getProgressCardsData(mixedItemDist!, "Progress", true, t, theme);

	const weakTagIds = needsImprovementProgressData
		? needsImprovementProgressData.items.map((item) => item.id!)
		: [];

	const handleNavigation = (numericId: number) => {
		router.push(`/session/${numericId}/report/`);
	};

	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog(!showSessionConfigDialog);
	};

	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};

	const handleClickInfo = (text: string, title: string) => {
		router.push({
			pathname: "/info",
			params: {
				body: text,
				title: title,
			},
		});
	};

	const handleClickStudy = () => {
		const tagIds = needsImprovementProgressData
			? needsImprovementProgressData.items.map((item) => item.id!)
			: [];
		handleSessionConfigNavigation(toggleSessionConfigDialog, {
			selectedTagIds: tagIds,
			reset: "true",
			studyRecommenedTopics: "true",
		});
	};

	const handleClickCard = (title: string) => {
		router.push({
			pathname: "/progress/detailed-list/mastery",
			params: {
				type: title,
			},
		});
	};

	return (
		<>
			<ProgressApplet
				renderNestedExamChips={() => (
					<NestedExamChipsContainer containerStyle={{ paddingVertical: 24 }} />
				)}
				showPracticeTests={showPracticeTests}
				handleClickCard={handleClickCard}
				handleClickInfo={handleClickInfo}
				showStudyPackViews={showStudyPackViews}
				handleClickStudy={handleClickStudy}
				practiceTestsCardsData={practiceTestsCardsData}
				progressPageSummaryCardData={progressPageSummaryCardData}
				appFont={appFont}
				handleNavigation={handleNavigation}
				progressNeedsImprovementCtaLabel={t("progress.needsImprovement.cta")}
				emptyState={{
					title: t("emptyState.progress.title"),
					subtitle: t("emptyState.progress.subtitle"),
					cta: t("emptyState.progress.cta"),
					onAction: () => {
						if (showStudyPackViews) {
							handleSessionConfigNavigation(toggleSessionConfigDialog);
						} else {
							router.push("/home");
						}
					},
				}}
				masteryTagsProgressData={masteryTagsProgressData}
				needsImprovementProgressData={needsImprovementProgressData}
				showEmptyState={showEmptyState}
				weakTagIds={weakTagIds.map((id) => id.toString())}
			/>
			{showSessionConfigDialog && weakTagIds ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
			{showSessionConfigDialog && !weakTagIds ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
			{isWeb && (
				<FeedbackDialogContainer
					visible={showFeedbackDialog}
					onDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</>
	);
};

export default ProgressContainer;
